import React, { useState, useEffect} from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Container } from "../../components/ui/Container";
import { Stack } from "../../components/ui/Stack";
import { Heading } from "../../components/ui/Heading";
import { Card } from "../../components/ui/Card";
import { Spinner } from "../../components/ui/Spinner";
import { Alert } from "../../components/ui/Alert";
import { useApi } from "../../context/ApiContext";
import {
  FaUser,
} from "react-icons/fa";
import { getResizedImageUrl, ImagePresets } from "../../utils/imageUtils";
import { useStoreLogoPlaceholder } from "../../hooks/useStoreLogoPlaceholder";
import { findProductImage } from "../../utils/productImageUtils";
import {
  calculateOriginalPriceForOrderItem,
  calculateOrderItemDiscount,
} from "../../utils/orderPricingUtils";


export const CartDetails: React.FC = () => {
  const { t } = useTranslation();
  const { cartId } = useParams<{ cartId: string }>();
  const navigate = useNavigate();
  const { api } = useApi();
  const storeLogoPlaceholder = useStoreLogoPlaceholder();
  const [cart, setCart] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (cartId) {
      fetchCartById(cartId);
    }
  }, [cartId]);

  const fetchCartById = async (cartId: string) => {
    try {
      setLoading(true);
      const response = await api.get(`/cart/admin/${cartId}`);
      setCart(response.data);
      setLoading(false)
    } catch (err: any) {
      setError(err.response?.data?.error || t("admin.cartDetail.errors.fetch"));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <Stack align="center" spacing="lg">
          <Spinner size="lg" />
        </Stack>
      </Container>
    );
  }

  if (!cart) {
    return (
      <Container>
        <Alert variant="error">{t("admin.cartDetail.notFound")}</Alert>
      </Container>
    );
  }

  return (
    <Container>
      <Stack spacing="md">
        <div className="flex items-center justify-between">
          <div>
            <Heading size="xl" className="mb-1">
              {t("admin.cartDetail.title")}: {cart.cartNumber}
            </Heading>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span>•</span>
              <span>
                {new Date(
                  cart.createdAt,
                ).toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>

        {error && <Alert variant="error">{error}</Alert>}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          <div className="lg:col-span-2 space-y-3">

            <Card padding="sm">
              <Stack spacing="sm">
                <Heading size="sm" className="text-base">
                  {t("admin.cartDetail.cartItems")}
                </Heading>
                <div className="space-y-2">
                  {cart.items?.map((item: any, index: number) => {
                    const productTitle =
                      item.productTitle ||
                      item.product?.title ||
                      t("admin.cartDetails.product");
                    const variantName = item.variantName || item.variant?.name;

                    // Calculate original price dynamically based on product pricing layers
                    const originalPrice = calculateOriginalPriceForOrderItem(
                      item.product,
                      item.selectedVariantOptions,
                      cart.locationId,
                    );

                    // Calculate discount information
                    const discountInfo = calculateOrderItemDiscount(
                      item.product,
                      item.price,
                      item.selectedVariantOptions,
                      cart.locationId,
                    );

                    // Find product image based on variant options
                    const image =
                      item.product &&
                      item.product.images &&
                      item.product.images.length > 0
                        ? findProductImage(
                            item.product,
                            item.selectedVariantOptions,
                          )
                        : undefined;
                    const imageUrl = image
                      ? getResizedImageUrl(
                          image.url,
                          ImagePresets.checkoutReview,
                          storeLogoPlaceholder,
                        )
                      : storeLogoPlaceholder;
                    const productUrl = item.product
                      ? `/products/${item.product._id}`
                      : null;
                    return (
                      <div
                        key={item._id || index}
                        className="flex gap-2 p-2 border border-gray-200 rounded"
                      >
                        {productUrl ? (
                          <Link
                            to={productUrl}
                            className="w-12 h-12 object-cover rounded flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                          >
                            <img
                              src={imageUrl}
                              alt={productTitle}
                              className="w-12 h-12 object-cover rounded flex-shrink-0"
                            />
                          </Link>
                        ) : (
                          <img
                            src={imageUrl}
                            alt={productTitle}
                            className="w-12 h-12 object-cover rounded flex-shrink-0"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          {productUrl ? (
                            <Link
                              to={productUrl}
                              className="font-medium text-sm hover:text-blue-600 transition-colors"
                            >
                              {productTitle}
                            </Link>
                          ) : (
                            <div className="font-medium text-sm">
                              {productTitle}
                            </div>
                          )}
                          <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-500 mt-0.5">
                            {variantName && (
                              <span>
                                {t("admin.cartDetail.variant")}: {variantName}
                              </span>
                            )}
                            {item.sku && (
                              <span>
                                {t("admin.cartDetail.sku")}: {item.sku}
                              </span>
                            )}
                            <span>
                              {t("admin.cartDetail.quantity")}: {item.quantity}
                            </span>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className="text-sm font-medium text-gray-900">
                            {cart.currency || "$"}
                            {(item.price * item.quantity).toFixed(2)}
                          </div>
                          {/* Show original price if discounted */}
                          {originalPrice > item.price && (
                            <div className="text-xs text-gray-500 line-through">
                              {cart.currency || "$"}
                              {(originalPrice * item.quantity).toFixed(2)}
                            </div>
                          )}
                          <div className="text-xs text-gray-500">
                            {cart.currency || "$"}
                            {item.price.toFixed(2)} each
                            {/* Show discount info */}
                            {discountInfo.discountPerUnit > 0 && (
                              <div className="text-green-600 text-xs mt-1">
                                <div>
                                  Discount: -
                                  {discountInfo.discountPerUnit.toFixed(2)}{" "}
                                  {cart.currency || "$"}
                                  {discountInfo.discountType ===
                                    "percentage" && (
                                    <span className="text-xs">
                                      ({discountInfo.discountValue || 0}%)
                                    </span>
                                  )}
                                </div>
                                <div className="text-gray-500 text-xs">
                                  Applied: Product level
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Stack>
            </Card>

            <Card padding="sm">
              <Stack spacing="sm">
                <Heading size="sm" className="text-base">
                  Customer & Addresses
                </Heading>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {cart.user && (
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <div className="text-xs font-medium text-gray-500">
                          {t("admin.cartDetail.customerInformation")}
                        </div>
                        <button
                          onClick={() =>
                            navigate(`/admin/users/${cart.user._id}`)
                          }
                          className="p-1 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded"
                          title={
                            t("admin.cartDetail.viewUserProfile") ||
                            "View User Profile"
                          }
                        >
                          <FaUser size={12} />
                        </button>
                      </div>
                      <div className="text-xs text-gray-700 space-y-0.5">
                        <div className="font-medium">{cart.user.name}</div>
                        <div className="text-gray-600">{cart.email}</div>
                      </div>
                    </div>
                  )}
                </div>
              </Stack>
            </Card>
          </div>

          <div className="space-y-3">

            {/* Order Summary - Lower priority, moved to bottom */}
            <Card padding="sm">
              <Stack spacing="sm">
                <Heading size="sm" className="text-base">
                  {t("admin.cartDetail.orderSummary")}
                </Heading>
                <div className="space-y-1.5 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">
                      {t("admin.cartDetail.subtotal")}:
                    </span>
                    <span className="text-gray-900">
                      {cart.currency || "$"}
                      {cart?.subtotal?.toFixed(2)}
                    </span>
                  </div>
                  {/* Show product-level discounts if any */}
                  {cart.items?.some((item: any) => {
                    const originalPrice = calculateOriginalPriceForOrderItem(
                      item.product,
                      item.selectedVariantOptions,
                      cart.locationId,
                    );
                    return originalPrice > item.price;
                  }) && (
                    <div className="flex justify-between">
                      <span className="text-green-600">
                        {t("admin.cartDetail.itemDiscounts") ||
                          "Product Discounts"}
                        :
                      </span>
                      <span className="text-green-600">
                        -{cart.currency || "$"}
                        {cart.items
                          .filter((item: any) => {
                            const originalPrice =
                              calculateOriginalPriceForOrderItem(
                                item.product,
                                item.selectedVariantOptions,
                                cart.locationId,
                              );
                            return originalPrice > item.price;
                          })
                          .reduce((sum: number, item: any) => {
                            const originalPrice =
                              calculateOriginalPriceForOrderItem(
                                item.product,
                                item.selectedVariantOptions,
                                cart.locationId,
                              );
                            return (
                              sum + (originalPrice - item.price) * item.quantity
                            );
                          }, 0)
                          .toFixed(2)}
                      </span>
                    </div>
                  )}
                  {/* Show order-level discount if any */}
                  {cart.discount && cart.discount > 0 && (
                    <div className="flex justify-between">
                      <span className="text-green-600">
                        {t("admin.cartDetail.orderDiscount") ||
                          "Order Discount"}
                        :
                      </span>
                      <span className="text-green-600">
                        -{cart.currency || "$"}
                        {cart.discount.toFixed(2)}
                        {cart.discountType === "percentage" && (
                          <span className="text-xs ml-1">
                            ({cart.discountValue || 0}%)
                          </span>
                        )}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">
                      {t("admin.cartDetail.tax")}:
                    </span>
                    <span className="text-gray-900">
                      {cart.currency || "$"}
                      {cart?.tax?.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">
                      {t("admin.cartDetail.shipping")}:
                    </span>
                    <span className="text-gray-900">
                      {cart.currency || "$"}
                      {cart?.shipping?.toFixed(2)}
                    </span>
                  </div>
                  <hr className="my-1.5" />
                  <div className="flex justify-between font-semibold text-base">
                    <span>{t("admin.total")}:</span>
                    <span>
                      {cart.currency || "$"}
                      {cart?.total?.toFixed(2)}
                    </span>
                  </div>
                </div>
              </Stack>
            </Card>
          </div>
        </div>
      </Stack>
    </Container>
  );
};

export default CartDetails;
